import { PERMISSIONS } from "../../ts-types/apiTypes";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { useLogoutFunction } from "@propelauth/react";
import type { User } from "@propelauth/react";

import type { MeApiResponse } from "../../ts-types/apiTypes";
import { useAuth } from "../../utils/context";

import "./DebugTopBanner.scss";
import {
  Icon,
  MultiDropdown,
  type MultiDropdownItem,
  type MultiDropdownSelectedItem,
} from "@/index";

const PROD_ENVS = new Set(["prd", "prod", "production", "prdeu"]);
const ENV_OPTIONS = [
  { value: "local", label: "Local" },
  { value: "dev", label: "Development" },
  { value: "stg", label: "Staging" },
  { value: "prd", label: "Production" },
  { value: "prdeu", label: "Production EU" },
] as const;
const MEMBERSHIP_TYPES = ["member", "admin", "owner"] as const;
const GITHUB_COMMIT_BASE_URL =
  "https://github.com/pingintel/boxandwhisker-sov-scrubber/commit/";
const STICKY_STORAGE_KEY = "DebugTopBanner.sticky";

type DebugAppKind = "maps" | "sal" | "vision" | "portal" | "ops";
type MembershipType = (typeof MEMBERSHIP_TYPES)[number];
type OpenMenu = "user" | null;

type ServerInfo = {
  product?: string;
  serverspace?: string;
  serverset?: string;
  gateway?: string;
  gitrev?: string | null;
};

type DebugMembership = {
  uuid: string;
  entity_type: "company" | "division" | "team" | null;
  entity_uuid: string | null;
  entity_name: string | null;
  membership_type: MembershipType | string;
  temporary_override?: boolean | null;
  company_short_name?: string | null;
  company_name?: string | null;
  division_short_name?: string | null;
  division_name?: string | null;
  team_name?: string | null;
};

type DebugTeam = {
  uuid: string;
  name: string;
  division_uuid: string;
  division_name: string;
  division_short_name: string;
  company_uuid: string;
  company_name: string;
  company_short_name: string;
};

type DebugDivision = {
  uuid: string;
  name: string;
  short_name: string;
  teams: DebugTeam[];
};

type DebugOrganization = {
  uuid: string;
  name: string;
  short_name: string;
  divisions: DebugDivision[];
};

type DeveloperToolsContext = {
  organizations: DebugOrganization[];
  memberships: DebugMembership[];
  temporary_memberships: DebugMembership[];
  primary_team_uuid: string | null;
};

type DebugMe = Partial<MeApiResponse> & {
  team_id?: string | null;
  company_short_name?: string | null;
  company_id?: string | null;
  division_id?: string | null;
  memberships?: DebugMembership[];
  temporary_memberships?: DebugMembership[];
  primary_team_id?: string | null;
  can_view_all_teams?: boolean;
  canAccessDeveloperTools?: boolean;
  canAccessPrism?: boolean;
  impersonator_user_id?: string | null;
  impersonator_email?: string | null;
};

type EntityOption = {
  value: string;
  label: string;
};

type DebugTopBannerProps = {
  apiBaseUrl?: string;
  app?: DebugAppKind;
  accessToken?: string | null;
  ssoUser?: User | null;
  me?: Partial<MeApiResponse>;
  isImpersonating?: boolean | null;
  impersonatorUserId?: string | null;
  meEndpoint?: string;
  serverInfoEndpoint?: string;
  navigateToUrl?: (url: string) => void;
  /**
   * App-specific controls rendered in the right side of the full developer
   * banner. Only shown to users with developer-tools access. Used by
   * e.g. Portal to add custom debug toggles.
   */
  extraControls?: ReactNode;
};

const getCurrentEnv = () => String(import.meta.env?.VITE_APP_ENV ?? "local");

const normalizeEnvironment = (env: string | undefined | null) => {
  const normalized = String(env ?? "").toLowerCase();
  if (["prod", "production"].includes(normalized)) return "prd";
  if (["stage", "staging"].includes(normalized)) return "stg";
  if (["prod-eu", "production-eu", "prd-eu"].includes(normalized)) {
    return "prdeu";
  }
  if (normalized === "development") return "dev";
  return normalized;
};

const isProdEnv = (env: string | undefined | null) =>
  PROD_ENVS.has(normalizeEnvironment(env));

const joinUrl = (baseUrl: string, path: string) => {
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return new URL(path.replace(/^\//, ""), normalizedBaseUrl).toString();
};

const requestJson = async <T,>(
  url: string,
  init?: RequestInit,
): Promise<T | null> => {
  try {
    const response = await fetch(url, { credentials: "include", ...init });
    if (!response.ok) return null;
    return response.json() as Promise<T>;
  } catch {
    return null;
  }
};

const getAuthHeaders = (accessToken?: string | null) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return headers;
};

const shortGitRevision = (gitrev?: string | null) => {
  if (!gitrev) return null;
  return gitrev.length > 7 ? gitrev.slice(0, 7) : gitrev;
};

const isLikelyUrl = (value: string | null) =>
  !!value && /^https?:\/\//i.test(value);

const getEnvironmentLabel = (env: string) => {
  const normalizedEnv = normalizeEnvironment(env);
  return (
    ENV_OPTIONS.find((option) => option.value === normalizedEnv)?.label ?? env
  );
};

const getEnvironmentBaseUrl = (env: string, app: DebugAppKind) => {
  const normalizedEnv = normalizeEnvironment(env);
  const urls: Record<string, Record<DebugAppKind, string>> = {
    local: {
      maps: "http://local.pingintel.com:3010",
      sal: "http://local.pingintel.com:3010",
      vision: "http://vision.local.pingintel.com:3012",
      portal: "http://local.pingintel.com:3015",
      ops: "http://localhost:3016",
    },
    dev: {
      maps: "https://app.dev.pingintel.com",
      sal: "https://app.dev.pingintel.com",
      vision: "https://vision.dev.pingintel.com",
      portal: "https://vision.dev.pingintel.com",
      ops: "https://ops.pingintel.com",
    },
    stg: {
      maps: "https://app.staging.pingintel.com",
      sal: "https://app.staging.pingintel.com",
      vision: "https://vision.staging.pingintel.com",
      portal: "https://vision.staging.pingintel.com",
      ops: "https://ops.pingintel.com",
    },
    prd: {
      maps: "https://app.pingintel.com",
      sal: "https://app.pingintel.com",
      vision: "https://vision.pingintel.com",
      portal: "https://vision.pingintel.com",
      ops: "https://ops.pingintel.com",
    },
    prdeu: {
      maps: "https://app.eu.pingintel.com",
      sal: "https://app.eu.pingintel.com",
      vision: "https://vision.eu.pingintel.com",
      portal: "https://vision.eu.pingintel.com",
      ops: "https://ops.pingintel.com",
    },
  };

  return urls[normalizedEnv]?.[app] ?? urls.dev[app];
};

const detectApp = (app?: DebugAppKind): DebugAppKind => {
  if (
    app === "maps" &&
    /\/(activity|sal|sovfixer\/activity)\b/.test(window.location.pathname)
  ) {
    return "sal";
  }
  return app ?? "maps";
};

const buildEnvironmentJumpUrl = (env: string, app: DebugAppKind) => {
  const currentUrl = new URL(window.location.href);
  const targetUrl = new URL(getEnvironmentBaseUrl(env, app));
  targetUrl.pathname = currentUrl.pathname;
  targetUrl.search = currentUrl.search;
  targetUrl.hash = currentUrl.hash;
  return targetUrl.toString();
};

const defaultNavigateToUrl = (url: string) => {
  window.location.assign(url);
};

const extractPingId = () => {
  const url = new URL(window.location.href);
  const queryPingId =
    url.searchParams.get("pingid") || url.searchParams.get("pingId");
  if (queryPingId) return queryPingId;

  const match = url.pathname.match(/\/(?:s|i|success)\/([0-9A-Za-z-]+)/);
  return match?.[1] ?? null;
};

const extractSovid = () => {
  const url = new URL(window.location.href);
  const querySovid =
    url.searchParams.get("sovid") || url.searchParams.get("sovId");
  if (querySovid && querySovid !== "pli") return querySovid;

  const itemKey =
    url.searchParams.get("item_key") || url.searchParams.get("itemKey");
  const itemKeySovid = itemKey?.match(/i-(s-[0-9A-Za-z-]+)/)?.[1];
  if (itemKeySovid) return itemKeySovid;

  const pathSovid = url.pathname.match(
    /\/(?:maps|sov-report)\/(s-[0-9A-Za-z-]+)/,
  )?.[1];
  return pathSovid ?? null;
};

const extractOptionalLinks = () => {
  const params = new URL(window.location.href).searchParams;
  const logUrl =
    params.get("log") ||
    params.get("log_url") ||
    params.get("logUrl") ||
    params.get("seq");
  const s3Url = params.get("s3") || params.get("s3_url") || params.get("s3Url");
  return {
    logUrl: isLikelyUrl(logUrl) ? logUrl : null,
    s3Url: isLikelyUrl(s3Url) ? s3Url : null,
  };
};

const flattenEntityOptions = (
  context: DeveloperToolsContext | null,
): EntityOption[] => {
  if (!context) return [];

  return context.organizations.flatMap((company) => [
    {
      value: `company:${company.uuid}`,
      label: `Company: ${company.name} (${company.short_name})`,
    },
    ...company.divisions.flatMap((division) => [
      {
        value: `division:${division.uuid}`,
        label: `${company.short_name} / Division: ${division.name} (${division.short_name})`,
      },
      ...division.teams.map((team) => ({
        value: `team:${team.uuid}`,
        label: `${company.short_name} / ${division.short_name} / Team: ${team.name}`,
      })),
    ]),
  ]);
};

const formatMembership = (membership: DebugMembership) => {
  const level = membership.entity_type ?? "membership";
  const name =
    membership.entity_name ??
    membership.team_name ??
    membership.division_name ??
    membership.company_name;
  return `${level} ${name ?? "unknown"} (${membership.membership_type})`;
};

const getDeveloperPermission = (me: DebugMe | undefined) => {
  return Boolean(
    me?.permissions?.includes(PERMISSIONS.CanAccessDeveloperTools) ||
    me?.canAccessDeveloperTools ||
    me?.canAccessPrism ||
    me?.can_view_all_teams,
  );
};

const getDisplayEmail = (ssoUser?: User | null, me?: DebugMe) => {
  return ssoUser?.email ?? me?.email ?? me?.username ?? "unknown user";
};

export const DebugTopBanner = ({
  apiBaseUrl,
  app,
  accessToken: accessTokenProp,
  ssoUser: ssoUserProp,
  me,
  isImpersonating: isImpersonatingProp,
  impersonatorUserId: impersonatorUserIdProp,
  meEndpoint = "api/v1/me",
  serverInfoEndpoint = "api/v1/serverinfo",
  navigateToUrl = defaultNavigateToUrl,
  extraControls,
}: DebugTopBannerProps) => {
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const auth = useAuth();
  const logout = useLogoutFunction();
  const [serverInfo, setServerInfo] = useState<ServerInfo | null>(null);
  const [fetchedMe, setFetchedMe] = useState<DebugMe | null>(null);
  const [context, setContext] = useState<DeveloperToolsContext | null>(null);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedMembershipType, setSelectedMembershipType] =
    useState<MembershipType>("member");
  const [entitySearch, setEntitySearch] = useState("");
  const [manualClosed, setManualClosed] = useState(false);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [isSticky, setIsSticky] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STICKY_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const accessToken = accessTokenProp ?? auth.accessToken;
  const ssoUser = ssoUserProp ?? auth.ssoUser;
  const isImpersonating = Boolean(isImpersonatingProp ?? auth.isImpersonating);
  const impersonatorUserId = impersonatorUserIdProp ?? auth.impersonatorUserId;
  const effectiveMe = (fetchedMe ?? me) as DebugMe | undefined;
  const effectiveApp = detectApp(app);
  const baseUrl = apiBaseUrl ?? `${window.location.origin}/`;
  const currentEnv = normalizeEnvironment(
    serverInfo?.serverspace ?? getCurrentEnv(),
  );
  const prod = isProdEnv(currentEnv);
  const canAccessDeveloperTools = getDeveloperPermission(effectiveMe);
  const hasDeveloperControls = canAccessDeveloperTools;
  const entityOptions = useMemo(() => flattenEntityOptions(context), [context]);
  const filteredEntityOptions = useMemo(() => {
    const q = entitySearch.trim().toLowerCase();
    if (!q) return entityOptions;
    return entityOptions.filter((option) =>
      option.label.toLowerCase().includes(q),
    );
  }, [entityOptions, entitySearch]);
  const temporaryMemberships =
    context?.temporary_memberships ?? effectiveMe?.temporary_memberships ?? [];
  const hasTemporaryOverride = temporaryMemberships.length > 0;
  const showFullBanner =
    isImpersonating || hasDeveloperControls || hasTemporaryOverride;
  const shouldRender =
    !prod || isImpersonating || hasDeveloperControls || hasTemporaryOverride;
  const bannerVisible = shouldRender && !(manualClosed && showFullBanner);
  const pingId = useMemo(extractPingId, []);
  const sovid = useMemo(extractSovid, []);
  const optionalLinks = useMemo(extractOptionalLinks, []);

  useEffect(() => {
    let cancelled = false;
    requestJson<ServerInfo>(joinUrl(baseUrl, serverInfoEndpoint)).then(
      (data) => {
        if (!cancelled && data) setServerInfo(data);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [baseUrl, serverInfoEndpoint]);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    requestJson<DebugMe>(joinUrl(baseUrl, meEndpoint), {
      headers: getAuthHeaders(accessToken),
    }).then((data) => {
      if (!cancelled && data) setFetchedMe(data);
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, baseUrl, meEndpoint]);

  useEffect(() => {
    if (!accessToken || !hasDeveloperControls) return;
    let cancelled = false;
    requestJson<DeveloperToolsContext>(
      joinUrl(baseUrl, "api/v1/developer-tools/context"),
      {
        headers: getAuthHeaders(accessToken),
      },
    ).then((data) => {
      if (!cancelled && data) setContext(data);
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, baseUrl, hasDeveloperControls]);

  useEffect(() => {
    if (!selectedEntity && entityOptions.length) {
      setSelectedEntity(entityOptions[0].value);
    }
  }, [entityOptions, selectedEntity]);

  useEffect(() => {
    if (!bannerVisible) return undefined;

    const previousPaddingTop = document.body.style.paddingTop;
    document.body.style.paddingTop = previousPaddingTop
      ? `calc(${previousPaddingTop} + 4px)`
      : "4px";

    return () => {
      document.body.style.paddingTop = previousPaddingTop;
    };
  }, [bannerVisible]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!bannerRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenMenu(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const postJson = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      if (!accessToken) return false;
      const response = await fetch(joinUrl(baseUrl, path), {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(accessToken),
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        setActionStatus(`Request failed (${response.status})`);
        return false;
      }
      return true;
    },
    [accessToken, baseUrl],
  );

  const handleAddTemporaryMembership = useCallback(async () => {
    if (!selectedEntity) return;
    const [entityType, entityUuid] = selectedEntity.split(":");
    if (!entityType || !entityUuid) return;

    const ok = await postJson("api/v1/me/temporary-membership", {
      [`${entityType}_uuid`]: entityUuid,
      membership_type: selectedMembershipType,
    });
    if (ok) window.location.reload();
  }, [postJson, selectedEntity, selectedMembershipType]);

  const handleClearTemporaryMembership = useCallback(async () => {
    if (!accessToken) return;
    const response = await fetch(
      joinUrl(baseUrl, "api/v1/me/temporary-membership"),
      {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(accessToken),
      },
    );
    if (response.ok) {
      window.location.reload();
    } else {
      setActionStatus(`Clear override failed (${response.status})`);
    }
  }, [accessToken, baseUrl]);

  const handleEnvironmentChange = useCallback(
    (env: string) => {
      setOpenMenu(null);
      if (env === currentEnv) return;
      navigateToUrl(buildEnvironmentJumpUrl(env, effectiveApp));
    },
    [currentEnv, effectiveApp, navigateToUrl],
  );

  const handleUnimpersonate = useCallback(() => {
    void logout(false).finally(() => window.location.reload());
  }, [logout]);

  const handleToggleSticky = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      const button = event.currentTarget;
      setIsSticky((current) => {
        const next = !current;
        try {
          window.localStorage.setItem(STICKY_STORAGE_KEY, next ? "1" : "0");
        } catch {
          // ignore quota / privacy errors
        }
        if (!next) button?.blur?.();
        return next;
      });
    },
    [],
  );

  if (!bannerVisible) return null;

  if (!showFullBanner) {
    return (
      <div
        ref={bannerRef}
        className={`DebugTopBanner DebugTopBanner--readonly${isSticky ? " DebugTopBanner--sticky" : ""}`}
      >
        <button
          type="button"
          className={`DebugTopBanner__sticky${isSticky ? " DebugTopBanner__sticky--active" : ""}`}
          onClick={handleToggleSticky}
          title={isSticky ? "Unpin debug banner" : "Pin debug banner"}
          aria-pressed={isSticky}
        >
          <Icon
            iconName="Pin"
            size={14}
            fill={isSticky ? "#ffb703" : "var(--ping-color-neutral-300)"}
            color={isSticky ? "#ffb703" : "var(--ping-color-neutral-300)"}
          />
        </button>
        <span className="DebugTopBanner__readonlyPill">
          {getEnvironmentLabel(currentEnv)}
        </span>
      </div>
    );
  }

  const gitRevision = shortGitRevision(serverInfo?.gitrev);
  const realUsername = isImpersonating
    ? effectiveMe?.impersonator_email ||
      effectiveMe?.impersonator_user_id ||
      impersonatorUserId ||
      "impersonator"
    : getDisplayEmail(ssoUser, effectiveMe);
  const impersonatedUsername = isImpersonating
    ? getDisplayEmail(ssoUser, effectiveMe)
    : null;
  const visionBaseUrl = getEnvironmentBaseUrl(currentEnv, "vision");
  const mapsBaseUrl = getEnvironmentBaseUrl(currentEnv, "maps");
  const salBaseUrl = getEnvironmentBaseUrl(currentEnv, "sal");
  const temporaryMembershipLabel = temporaryMemberships.length
    ? temporaryMemberships.map(formatMembership).join(", ")
    : null;
  const accountPillLabel =
    temporaryMembershipLabel ??
    impersonatedUsername ??
    getDisplayEmail(ssoUser, effectiveMe);

  const entityItems: MultiDropdownItem[] = filteredEntityOptions.map(
    (option) => ({
      id: option.value,
      label: option.label,
      isActive: option.value === selectedEntity,
      onClick: () => setSelectedEntity(option.value),
      behavior: "CLOSE",
    }),
  );
  const selectedEntityOption = entityOptions.find(
    (option) => option.value === selectedEntity,
  );
  const selectedEntityItems: MultiDropdownSelectedItem[] = selectedEntityOption
    ? [{ value: selectedEntityOption.value, label: selectedEntityOption.label }]
    : [];
  const membershipTypeItems: MultiDropdownItem[] = MEMBERSHIP_TYPES.map(
    (membershipType) => ({
      id: membershipType,
      label: membershipType,
      isActive: membershipType === selectedMembershipType,
      onClick: () => setSelectedMembershipType(membershipType),
      behavior: "CLOSE",
    }),
  );
  const selectedMembershipItems: MultiDropdownSelectedItem[] = [
    { value: selectedMembershipType, label: selectedMembershipType },
  ];

  return (
    <div
      ref={bannerRef}
      className={`DebugTopBanner${openMenu ? " DebugTopBanner--open" : ""}${isSticky ? " DebugTopBanner--sticky" : ""}`}
    >
      <div className="DebugTopBanner__inner">
        <div className="DebugTopBanner__left">
          <button
            type="button"
            className={`DebugTopBanner__sticky${isSticky ? " DebugTopBanner__sticky--active" : ""}`}
            onClick={handleToggleSticky}
            title={isSticky ? "Unpin debug banner" : "Pin debug banner"}
            aria-pressed={isSticky}
          >
            <Icon
              iconName="Pin"
              size={14}
              fill={isSticky ? "#ffb703" : "var(--ping-color-neutral-300)"}
              color={isSticky ? "#ffb703" : "var(--ping-color-neutral-300)"}
            />
          </button>
          <select
            aria-label="Switch environment"
            className="DebugTopBanner__pill DebugTopBanner__envSelect"
            value={currentEnv}
            onChange={(event) => handleEnvironmentChange(event.target.value)}
          >
            {!ENV_OPTIONS.some((option) => option.value === currentEnv) ? (
              <option value={currentEnv}>
                {getEnvironmentLabel(currentEnv)}
              </option>
            ) : null}
            {ENV_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {gitRevision ? (
            <a
              className="DebugTopBanner__metaLink"
              href={`${GITHUB_COMMIT_BASE_URL}${serverInfo?.gitrev}`}
              target="_blank"
              rel="noreferrer"
            >
              <span>⚑</span> {gitRevision}
            </a>
          ) : null}
          {serverInfo?.serverset ? (
            <span className="DebugTopBanner__metaItem">
              ▤ {serverInfo.serverset}
            </span>
          ) : null}
          {optionalLinks.s3Url ? (
            <a
              className="DebugTopBanner__metaLink"
              href={optionalLinks.s3Url}
              target="_blank"
              rel="noreferrer"
            >
              ⛁ s3
            </a>
          ) : null}
          {optionalLinks.logUrl ? (
            <a
              className="DebugTopBanner__metaLink"
              href={optionalLinks.logUrl}
              target="_blank"
              rel="noreferrer"
            >
              ▣ logs
            </a>
          ) : null}
          {pingId ? (
            <a
              className="DebugTopBanner__metaLink"
              href={`${visionBaseUrl}/s/${pingId}`}
              target="_blank"
              rel="noreferrer"
            >
              {pingId}
            </a>
          ) : null}
          {sovid ? (
            <span className="DebugTopBanner__metaItem">
              {sovid}:{" "}
              <a
                href={`${salBaseUrl}/sovfixer/activity?rowDetails=${encodeURIComponent(sovid)}`}
              >
                SAL
              </a>{" "}
              ·{" "}
              <a href={`${mapsBaseUrl}/new/maps/${encodeURIComponent(sovid)}`}>
                maps
              </a>
            </span>
          ) : null}
          {serverInfo?.product ? (
            <span className="DebugTopBanner__metaItem DebugTopBanner__product">
              {serverInfo.product}
            </span>
          ) : null}
        </div>

        <div className="DebugTopBanner__right">
          {hasDeveloperControls ? extraControls : null}
          <div className="DebugTopBanner__menuAnchor DebugTopBanner__menuAnchor--right">
            <button
              type="button"
              className="DebugTopBanner__pill DebugTopBanner__accountButton"
              onClick={() =>
                setOpenMenu((current) => (current === "user" ? null : "user"))
              }
            >
              <span className="DebugTopBanner__icon">♚</span>
              <span className="DebugTopBanner__truncate">
                {accountPillLabel}
              </span>
              {isImpersonating ? (
                <span className="DebugTopBanner__muted">↔ {realUsername}</span>
              ) : null}
            </button>

            {openMenu === "user" ? (
              <div className="DebugTopBanner__menu DebugTopBanner__menu--account">
                <div className="DebugTopBanner__menuSection">
                  <div className="DebugTopBanner__menuEyebrow">Signed in</div>
                  <div className="DebugTopBanner__menuText">
                    {getDisplayEmail(ssoUser, effectiveMe)}
                  </div>
                  {isImpersonating ? (
                    <div className="DebugTopBanner__menuText DebugTopBanner__dangerText">
                      👻 Real user: {realUsername}
                    </div>
                  ) : null}
                </div>

                {temporaryMembershipLabel ? (
                  <div className="DebugTopBanner__menuSection">
                    <div className="DebugTopBanner__menuEyebrow">
                      Impersonating As
                    </div>
                    <div className="DebugTopBanner__menuText">
                      {temporaryMembershipLabel}
                    </div>
                    {accessToken ? (
                      <button
                        type="button"
                        className="DebugTopBanner__menuAction"
                        onClick={handleClearTemporaryMembership}
                      >
                        Clear temporary permissions
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {hasDeveloperControls &&
                !temporaryMembershipLabel &&
                entityOptions.length ? (
                  <div className="DebugTopBanner__menuSection">
                    <div className="DebugTopBanner__menuEyebrow">
                      Impersonate Membership
                    </div>
                    <MultiDropdown
                      items={entityItems}
                      selectedItems={selectedEntityItems}
                      placeholder="Select company / division / team"
                      onSearchChange={setEntitySearch}
                      matchTriggerWidth
                      usePortal={false}
                      triggerClasses="DebugTopBanner__multiTrigger"
                      menuClasses="DebugTopBanner__multiMenu"
                      menuItemClasses="DebugTopBanner__multiItem"
                    />
                    <div className="DebugTopBanner__menuInline">
                      <MultiDropdown
                        items={membershipTypeItems}
                        selectedItems={selectedMembershipItems}
                        matchTriggerWidth
                        usePortal={false}
                        triggerClasses="DebugTopBanner__multiTrigger"
                        menuClasses="DebugTopBanner__multiMenu"
                        menuItemClasses="DebugTopBanner__multiItem"
                      />
                      <button
                        type="button"
                        className="DebugTopBanner__menuAction"
                        onClick={handleAddTemporaryMembership}
                        disabled={!selectedEntity}
                      >
                        Apply
                      </button>
                    </div>
                    <div className="DebugTopBanner__hint">
                      Browse the app as this membership. Clear anytime to
                      revert.
                    </div>
                  </div>
                ) : null}

                {isImpersonating ? (
                  <button
                    type="button"
                    className="DebugTopBanner__menuItem DebugTopBanner__menuItem--danger"
                    onClick={handleUnimpersonate}
                  >
                    ⓧ Stop impersonation
                  </button>
                ) : null}
                {actionStatus ? (
                  <div className="DebugTopBanner__error">{actionStatus}</div>
                ) : null}
              </div>
            ) : null}
          </div>

          <button
            className="DebugTopBanner__close"
            type="button"
            onClick={() => setManualClosed(true)}
            title="Hide until refresh"
          >
            <Icon iconName="X" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};
